import {
  MAX_LIST_FILTERS,
  type AbilityRecord,
  type FieldManifest,
  type FilterOp,
  type OptionColor,
} from "../../shared/api/generated/contracts";

export interface ListFilter {
  field_id: string;
  op: FilterOp;
  value?: unknown;
}

interface ListConfig {
  sortBy?: string;
  sortDir: "asc" | "desc";
  groupBy?: string;
  filters: ListFilter[];
  subtitleFieldId?: string;
  badgeFieldId?: string;
  emptyText?: string;
}

interface RecordGroup {
  /** Raw group value as a string; "" for records without a value. */
  key: string;
  records: AbilityRecord[];
}

const FILTER_OPS: readonly FilterOp[] = [
  "eq",
  "neq",
  "empty",
  "not_empty",
  "before_today",
  "today",
  "after_today",
];

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/** Reads the optional list settings; anything malformed is ignored. */
export function parseListConfig(
  config: Record<string, unknown> | undefined,
): ListConfig {
  const rawFilters = Array.isArray(config?.filters) ? config.filters : [];
  const filters = rawFilters
    .flatMap((entry): ListFilter[] => {
      if (!entry || typeof entry !== "object") return [];
      const item = entry as Record<string, unknown>;
      const fieldId = text(item.field_id);
      const op = item.op as FilterOp;
      if (!fieldId || !FILTER_OPS.includes(op)) return [];
      return [{ field_id: fieldId, op, value: item.value }];
    })
    .slice(0, MAX_LIST_FILTERS);
  return {
    sortBy: text(config?.sort_by),
    sortDir: config?.sort_dir === "desc" ? "desc" : "asc",
    groupBy: text(config?.group_by),
    filters,
    subtitleFieldId: text(config?.subtitle_field_id),
    badgeFieldId: text(config?.badge_field_id),
    emptyText: text(config?.empty_text),
  };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Local calendar day, `YYYY-MM-DD`, of a clock reading. */
function localDay(now: Date): string {
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Local calendar day of a date or datetime value, or undefined. */
export function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : localDay(parsed);
}

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

function equals(value: unknown, expected: unknown): boolean {
  if (Array.isArray(value)) return value.includes(expected);
  return value === expected;
}

function matches(value: unknown, filter: ListFilter, today: string): boolean {
  switch (filter.op) {
    case "eq":
      return equals(value, filter.value);
    case "neq":
      return !equals(value, filter.value);
    case "empty":
      return isEmpty(value);
    case "not_empty":
      return !isEmpty(value);
    default: {
      const day = dayOf(value);
      if (day === undefined) return false;
      if (filter.op === "before_today") return day < today;
      if (filter.op === "today") return day === today;
      return day > today;
    }
  }
}

/** Filters are ANDed. `now` is injected so date ops are testable. */
export function applyFilters(
  records: readonly AbilityRecord[],
  filters: readonly ListFilter[],
  now: Date = new Date(),
): AbilityRecord[] {
  if (filters.length === 0) return [...records];
  const today = localDay(now);
  return records.filter((record) =>
    filters.every((filter) =>
      matches(record.payload[filter.field_id], filter, today),
    ),
  );
}

/** Records of one collection; all of them when the collection is unknown. */
export function recordsOfCollection(
  records: readonly AbilityRecord[],
  collectionId: string | undefined,
): AbilityRecord[] {
  return collectionId === undefined
    ? [...records]
    : records.filter((record) => record.collection_id === collectionId);
}

/** Finite number of a number or numeric-looking string, else undefined. */
function numericValue(value: unknown): number | undefined {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean")
    return Number(a) - Number(b);
  return String(a).localeCompare(String(b));
}

/**
 * Stable sort; records without a value always go last. Number fields compare
 * numerically, including numeric-looking strings; the rest use locale order.
 */
export function sortRecords(
  records: readonly AbilityRecord[],
  sortBy: string | undefined,
  direction: "asc" | "desc",
  fieldType?: FieldManifest["type"],
): AbilityRecord[] {
  if (!sortBy) return [...records];
  const sign = direction === "desc" ? -1 : 1;
  const numeric = fieldType === "number";
  const missing = (value: unknown) =>
    isEmpty(value) || (numeric && numericValue(value) === undefined);
  return [...records].sort((left, right) => {
    const a = left.payload[sortBy];
    const b = right.payload[sortBy];
    const aMissing = missing(a);
    const bMissing = missing(b);
    if (aMissing || bMissing) return Number(aMissing) - Number(bMissing);
    return numeric
      ? sign * ((numericValue(a) ?? 0) - (numericValue(b) ?? 0))
      : sign * compareValues(a, b);
  });
}

/** Groups by first appearance; the group without a value comes last. */
export function groupRecords(
  records: readonly AbilityRecord[],
  field: Pick<FieldManifest, "id" | "type">,
): RecordGroup[] {
  const groups = new Map<string, AbilityRecord[]>();
  for (const record of records) {
    const raw = record.payload[field.id];
    const key = isEmpty(raw)
      ? ""
      : field.type === "date" || field.type === "datetime"
        ? (dayOf(raw) ?? "")
        : String(raw);
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }
  const ordered = [...groups.entries()].map(([key, items]) => ({
    key,
    records: items,
  }));
  return [
    ...ordered.filter((group) => group.key !== ""),
    ...ordered.filter((group) => group.key === ""),
  ];
}

/** The first `limit` records across the groups, in group order. */
export function pageGroups(
  groups: readonly RecordGroup[],
  limit: number,
): RecordGroup[] {
  let left = limit;
  const page: RecordGroup[] = [];
  for (const group of groups) {
    if (left <= 0) break;
    const taken = group.records.slice(0, left);
    left -= taken.length;
    page.push({ key: group.key, records: taken });
  }
  return page;
}

export const GROUPABLE_TYPES: readonly FieldManifest["type"][] = [
  "select",
  "boolean",
  "date",
];

const OPTION_COLORS: readonly OptionColor[] = [
  "neutral",
  "accent",
  "success",
  "warning",
  "error",
  "info",
];

/** Colour declared on a select option, if any. */
export function optionColorOf(
  field: Pick<FieldManifest, "config">,
  value: unknown,
): OptionColor | undefined {
  const raw = field.config?.options;
  if (!Array.isArray(raw)) return undefined;
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const option = entry as Record<string, unknown>;
    if ((option.value ?? option.id) !== value) continue;
    return OPTION_COLORS.includes(option.color as OptionColor)
      ? (option.color as OptionColor)
      : undefined;
  }
  return undefined;
}
